import { colors, cssThemedValue } from '@creditornot/cb-ingredients';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React, { ReactNode, useMemo } from 'react';
import styled from 'styled-components';

import { solidify } from 'modules/menu/solidify';
import { SelectionCategory } from 'modules/shared-menu/types';

import { MenuEditorDragData } from '../../dnd/types';
import { getMenuEditorDragEventData } from '../../dnd/utils';
import NavigationItem from '../NavigationItem';

type Props = {
  categoryId: string;
  label: ReactNode;
  language: string;
  menuEditorRootUrl: string;
  subcategory: SelectionCategory;
};

const Root = React.memo(styled.div<{ isDragging?: boolean }>`
  margin: -8px;
  padding-block-start: 8px;
  padding-block-end: 8px;
  opacity: ${({ isDragging }) => (isDragging ? 0.6 : 1)};
`);

const SubcategoryDragOverlay = styled(NavigationItem)`
  background-color: ${cssThemedValue({
    light: solidify(colors.pepper8, 'light'),
    dark: solidify(colors.salt8, 'dark'),
  })} !important;
`;

export const DndSubcategoryNavItem = React.memo(({ label, subcategory, menuEditorRootUrl, categoryId }: Props) => {
  const data: MenuEditorDragData = useMemo(
    () => ({
      type: 'DND_NAVIGATION_SUBCATEGORY',
      subcategory,
      parentCategoryId: categoryId,
      dragOverlay: (
        <Root>
          <SubcategoryDragOverlay label={label} numberOfItems={subcategory.offer_bindings?.length ?? 0} />
        </Root>
      ),
      shrinkOverlayTypes: [
        'DND_EDITOR_SINGLE_ITEM',
        'DND_EDITOR_MULTIPLE_ITEMS',
        'DND_EDITOR_SINGLE_NO_CATEGORY_ITEM',
        'DND_EDITOR_MULTIPLE_NO_CATEGORY_ITEMS',
      ],
    }),
    [categoryId, label, subcategory],
  );
  const sortable = useSortable({
    id: subcategory.id,
    data,
  });
  const dndStyle = useMemo(
    () => ({
      transform: CSS.Transform.toString(sortable.transform),
      transition: sortable.transition,
    }),
    [sortable.transform, sortable.transition],
  );
  const activeEvent = sortable.active && getMenuEditorDragEventData(sortable.active);
  const isDroppable = useMemo(
    () => !!activeEvent && data.shrinkOverlayTypes.includes(activeEvent.type) && sortable.isOver,

    [activeEvent, data.shrinkOverlayTypes, sortable.isOver],
  );
  return (
    <Root
      style={dndStyle}
      {...sortable.listeners}
      {...sortable.attributes}
      ref={sortable.setNodeRef}
      isDragging={sortable.isDragging}
    >
      <NavigationItem
        isDroppable={isDroppable}
        numberOfItems={subcategory.offer_bindings?.length ?? 0}
        label={label}
        href={`${menuEditorRootUrl}/${categoryId}/subcategories/${subcategory.id}`}
      />
    </Root>
  );
});
